import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export default function OtpEntry() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const load = () => api.get(`/public/otp/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const requestOtp = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/public/otp/${token}/request`); await load(); } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/public/otp/${token}`, { otp }); setDone(data); } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 space-y-5" data-testid="otp-entry-page">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-indigo-500" /><span className="font-head font-semibold">{info?.brand || "GSTDesk"} · GST OTP</span></div>
        {done ? (
          <div className="space-y-2" data-testid="otp-entry-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="font-medium">Thank you. GST connection is active.</p>
            <p className="text-sm text-muted-foreground">Valid until {new Date(done.valid_until).toLocaleDateString("en-GB").replaceAll("/", "-")}. You can close this page.</p>
          </div>
        ) : info?.needs_request ? (
          <div className="space-y-4" data-testid="otp-entry-renew">
            <div>
              <div className="font-head text-lg font-semibold" data-testid="otp-entry-client">{info.legal_name}</div>
              <div className="font-mono text-xs text-muted-foreground">GSTIN {info.gstin}</div>
            </div>
            <p className="text-sm text-muted-foreground">Your GST connection is ending soon. Keep your registered mobile at hand and click below; GSTN will send an OTP valid for about 10 minutes.</p>
            {info.test_mode && <p className="text-xs text-amber-600">Test mode: no real OTP is sent yet; any 6 digits will work.</p>}
            <button data-testid="otp-entry-request" onClick={requestOtp} disabled={busy} className="w-full rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2.5 text-sm font-medium">{busy ? "Sending…" : "Send OTP to my registered mobile"}</button>
          </div>
        ) : info ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="font-head text-lg font-semibold" data-testid="otp-entry-client">{info.legal_name}</div>
              <div className="font-mono text-xs text-muted-foreground">GSTIN {info.gstin}</div>
            </div>
            <p className="text-sm text-muted-foreground">Enter the 6-digit OTP sent by GSTN to your registered mobile. The connection stays active for 30 days.</p>
            {info.test_mode && <p className="text-xs text-amber-600" data-testid="otp-entry-test-note">Test mode: no real OTP is sent yet; any 6 digits will work.</p>}
            <input data-testid="otp-entry-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus
              className="w-full rounded-md bg-secondary/50 border border-border px-3 py-3 text-center text-xl font-mono tracking-[0.5em]" placeholder="______" />
            <button data-testid="otp-entry-submit" disabled={busy || otp.length !== 6} className="w-full rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2.5 text-sm font-medium">{busy ? "Verifying…" : "Submit OTP"}</button>
          </form>
        ) : !err && <p className="text-sm text-muted-foreground">Loading…</p>}
        {err && <p className="text-sm text-rose-600" data-testid="otp-entry-error">{err}</p>}
      </div>
    </div>
  );
}
